<template>
  <div class="md:h-full">
    <ErrorBoundary>
      <div v-if="articleContentHeadingArr.length > 0">
        <MagazineSidebarCard
          class="md:bg-white md:rounded-2xl md:border md:border-stone-75 md:drop-shadow-[0_2px_6px_rgba(0,0,0,0.06)] md:p-[17px]"
          :heading="$t('magazine.articlePage.tableOfContents')"
        >
          <MagazineSummaryList
            :items="articleContentHeadingArr"
            type="numbers"
          />

          <div
            v-if="partnerData"
            class="my-4 flex gap-4 items-center"
            data-test-id="article-sidebar__partner-data"
          >
            <div class="w-[100px] rounded-lg overflow-hidden">
              <NuxtImg
                v-if="supplierGetters.getLogo(partnerData)"
                class="w-full h-full"
                :src="String(supplierGetters.getLogo(partnerData))"
              />
            </div>
            <div>
              <NuxtLinkLocale
                class="text-base font-semibold hover:text-primary"
                :to="`/p/${supplierGetters.getPermalink(partnerData)}/`"
              >
                {{ supplierGetters.getName(partnerData) }}
              </NuxtLinkLocale>
              <div class="text-sm">
                {{ $t('magazine.articlePage.supplierProfileSubtitle') }}
              </div>
            </div>
          </div>
        </MagazineSidebarCard>

        <MagazineSocialIcons
          :image="articleGetters.getHeaderImageUrl(article)"
          :description="articleGetters.getDescription(article)"
          class="justify-center gap-3 pt-4 text-lime-800"
        />
      </div>

      <hr
        v-if="articleSupplierEvent && articleSupplierEvent.id"
        class="my-3 border-gray-600 md:hidden"
      />

      <div
        v-if="articleSupplierEvent && articleSupplierEvent.id"
        data-test-id="article-sidebar__featured-partner-event"
        class="h-full flex items-center my-8 md:m-0 justify-center md:justify-start"
      >
        <!-- Product card V2 (KFT-4152); the control branch is the rollback -->
        <ProductCardV2
          v-if="isProductCardV2 && articleSupplierEventItem"
          class="w-full h-fit"
          :product="articleSupplierEventItem"
          disable-wishlist
        />
        <ProductCard
          v-else
          class="w-full h-fit"
          :image="eventGetters.getFirstThumb(articleSupplierEvent)"
          :link="`/e/${eventGetters.getPermalink(articleSupplierEvent)}/`"
          :duration="
            fnGetFormattedDuration(
              eventGetters.getDefaultDuration(articleSupplierEvent)
            )
          "
          :rating="eventGetters.getReviewsRating(articleSupplierEvent)"
          :regular-price="
            eventGetters.getRegularPriceAmount(articleSupplierEvent)
          "
          :special-price="
            eventGetters.getSpecialPriceAmount(articleSupplierEvent)
          "
          :title="eventGetters.getTitle(articleSupplierEvent) || 'No title'"
          :is-teamevent="eventGetters.getIsTeamEvent(articleSupplierEvent)"
          :wishlist-icon="false"
        />
      </div>
    </ErrorBoundary>
  </div>
</template>
<script setup lang="ts">
import * as qs from 'qs'
import type { ComputedRef, Ref } from 'vue'
import type {
  StrapiMagazineArticle,
  StrapiMagazineArticleCategory,
} from '~/composables/types/strapi/strapi-magazine'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type {
  EventDescription,
  GalleryItem,
  Supplier,
} from '~/composables/types/event'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import ProductCardV2 from '~/components/product/ProductCardV2.vue'
import { useProductCardVariant } from '~/composables/product/useProductCardVariant'
import { parseEventDescriptionToSearchEngineEventItem } from '~/utils/helpers'

const article = inject('article') as ComputedRef<StrapiMagazineArticle>
const partnerData = inject('partnerData') as Ref<Supplier>

const { parsedArticleContent = '' } = defineProps<{
  parsedArticleContent?: string
  articlesCategory: StrapiMagazineArticleCategory
}>()

const fnGetFormattedDuration = (d) => useGetFormattedDuration(d)

const eventPermalink = articleGetters.getEventPermalink(article?.value)

const { data: event } = eventPermalink
  ? await useKftApiFetch<KftApiDefaultResponse<EventDescription>>(
      `v1/store/events/${eventPermalink}?${qs.stringify(
        {
          include: mergeKftEventDescriptionSettingsIncludes([]),
        },
        { arrayFormat: 'comma' }
      )}`,
      {
        transform: (res: KftApiDefaultResponse<EventDescription>) => {
          // delete videos from media and photos to avoid crawlers trying to index
          // urls that dot not work
          if (res?.data?.media) {
            res.data.media = res.data.media.filter(
              (media: GalleryItem) => media.media_type !== 'video'
            )
          }

          if (res?.data?.media) {
            res.data.media = res.data.media.filter(
              (media: GalleryItem) => media.media_type !== 'video'
            )
          }

          return res
        },
      }
    )
  : { data: ref(undefined) }

const articleSupplierEvent = computed(() =>
  event.value?.data?.id ? event.value.data : null
)

const { isV2: isProductCardV2 } = useProductCardVariant()

const articleSupplierEventItem = computed(() =>
  articleSupplierEvent.value
    ? (parseEventDescriptionToSearchEngineEventItem(
        articleSupplierEvent.value
      ) as SearchEngineEventItem)
    : null
)

const articleContentHeadingArr = computed(() =>
  [...parsedArticleContent.matchAll(/<h2.*?id="([^"]*?)".*?>(.+?)<\/h2>/g)].map(
    (x) => ({
      label: x[2].replace(/^\d+\.\s*/, ''), // Remove numbering from label
      link: `#${x[1]}`,
    })
  )
)
</script>
